import React from 'react'

import {
  BANNER_DESKTOP_MEDIA,
  BANNER_DESKTOP_SIZES,
  BANNER_MOBILE_MEDIA,
  BANNER_MOBILE_PRELOAD_TARGET_WIDTH,
  BANNER_MOBILE_SIZES,
} from 'constants/bannerImage'
import { getImageSrcSet, pickSrcFromSrcSet } from 'utils/bannerImageData'

interface LcpImageProps {
  image: any
  alt?: string
  className?: string
}

const renderAvifSources = (
  imageData: any,
  media: string,
  sizes: string
): React.ReactNode => {
  const srcSet = getImageSrcSet(imageData, 'image/avif')

  if (!srcSet) {
    return null
  }

  return (
    <source
      media={media}
      type="image/avif"
      srcSet={srcSet}
      sizes={sizes}
    />
  )
}

const renderWebpSources = (
  imageData: any,
  media: string,
  sizes: string
): React.ReactNode => {
  const srcSet = getImageSrcSet(imageData, 'image/webp')

  if (!srcSet) {
    return null
  }

  return (
    <source
      media={media}
      type="image/webp"
      srcSet={srcSet}
      sizes={sizes}
    />
  )
}

const LcpImage: React.FC<LcpImageProps> = ({
  image,
  alt = '',
  className = '',
}) => {
  const childImageSharp = image?.localFile?.childImageSharp
  const mobileData = childImageSharp?.mobile
  const desktopData = childImageSharp?.desktop
  const legacyData = childImageSharp?.gatsbyImageData

  if (mobileData?.images?.fallback && desktopData?.images?.fallback) {
    const mobileWebpSrcSet = getImageSrcSet(mobileData, 'image/webp')
    const mobileImgSrc =
      pickSrcFromSrcSet(mobileWebpSrcSet, BANNER_MOBILE_PRELOAD_TARGET_WIDTH) ??
      mobileData.images.fallback.src

    return (
      <picture className={className} data-lcp-image="">
        {renderAvifSources(mobileData, BANNER_MOBILE_MEDIA, BANNER_MOBILE_SIZES)}
        {renderWebpSources(mobileData, BANNER_MOBILE_MEDIA, BANNER_MOBILE_SIZES)}
        {renderAvifSources(
          desktopData,
          BANNER_DESKTOP_MEDIA,
          BANNER_DESKTOP_SIZES
        )}
        {renderWebpSources(
          desktopData,
          BANNER_DESKTOP_MEDIA,
          BANNER_DESKTOP_SIZES
        )}
        <img
          data-lcp-image=""
          src={mobileImgSrc}
          srcSet={mobileWebpSrcSet}
          sizes={BANNER_MOBILE_SIZES}
          alt={alt}
          loading="eager"
          decoding="async"
          {...({ fetchPriority: 'high' } as React.ImgHTMLAttributes<HTMLImageElement>)}
        />
      </picture>
    )
  }

  if (!legacyData?.images?.fallback) {
    return null
  }

  const { fallback } = legacyData.images
  const legacyWebpSrcSet = getImageSrcSet(legacyData, 'image/webp')
  const legacyAvifSrcSet = getImageSrcSet(legacyData, 'image/avif')

  return (
    <picture className={className} data-lcp-image="">
      {legacyAvifSrcSet && (
        <source type="image/avif" srcSet={legacyAvifSrcSet} sizes={BANNER_DESKTOP_SIZES} />
      )}
      {legacyWebpSrcSet && (
        <source type="image/webp" srcSet={legacyWebpSrcSet} sizes={BANNER_DESKTOP_SIZES} />
      )}
      <img
        data-lcp-image=""
        src={fallback.src}
        srcSet={legacyWebpSrcSet}
        sizes={BANNER_DESKTOP_SIZES}
        alt={alt}
        loading="eager"
        decoding="async"
        {...({ fetchPriority: 'high' } as React.ImgHTMLAttributes<HTMLImageElement>)}
      />
    </picture>
  )
}

export default LcpImage
